import React, { useState, useEffect } from 'react';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { Container, Header, Segment, Button, Icon, Message, Loader, List, Form, Input, Modal, Label } from 'semantic-ui-react';
import { TestUiClient } from './testUi.client';
import { DeleteButton } from '../components/DeleteButton';
export const TestUiPage = () => {
    const { getToken } = useAuth();
    const { organization } = useOrganization();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newTestName, setNewTestName] = useState('');
    const [creating, setCreating] = useState(false);
    useEffect(() => {
        const initializeClient = async () => {
            try {
                const testUiClient = new TestUiClient();
                setClient(testUiClient);
                const testList = await testUiClient.getTests();
                setTests(testList);
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to initialize');
            }
            finally {
                setLoading(false);
            }
        };
        initializeClient();
    }, [getToken, organization?.id]);
    const handleCreateTest = async () => {
        if (!client || !newTestName.trim())
            return;
        try {
            setCreating(true);
            const result = await client.createTest({
                testName: newTestName.trim(),
                path: '/api/example', // Default path
                method: 'GET' // Default method
            });
            // Navigate to the test detail page
            navigate(`/test-ui/${result.testId}`);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create test');
        }
        finally {
            setCreating(false);
            setCreateModalOpen(false);
            setNewTestName('');
        }
    };
    const handleDeleteTest = async (testId) => {
        if (!client || !confirm('Are you sure you want to delete this test?'))
            return;
        try {
            await client.deleteTest(testId);
            setTests(tests.filter(t => t.testId !== testId));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete test');
        }
    };
    if (loading) {
        return (React.createElement(Container, { fluid: true, style: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement(Segment, { basic: true, textAlign: "center" },
                React.createElement(Loader, { active: true, size: "large" }, "Loading tests..."))));
    }
    return (React.createElement(Container, { fluid: true, style: { minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '2rem' } },
        React.createElement(Container, null,
            React.createElement(Header, { as: "h1", icon: true, textAlign: "center", style: { marginBottom: '2rem' } },
                React.createElement(Icon, { name: "lab" }),
                React.createElement(Header.Content, null,
                    "API Test Suite",
                    React.createElement(Header.Subheader, null, "Manage and run tests for your API endpoints"))),
            error && (React.createElement(Message, { negative: true, onDismiss: () => setError(null), style: { marginBottom: '2rem' } },
                React.createElement(Message.Header, null, "Error"),
                React.createElement("p", null, error))),
            React.createElement(Segment, null,
                React.createElement(Header, { as: "h3", dividing: true },
                    React.createElement(Icon, { name: "list" }),
                    "Your Tests",
                    React.createElement(Button, { primary: true, floated: "right", size: "small", onClick: () => setCreateModalOpen(true) },
                        React.createElement(Icon, { name: "plus" }),
                        "New Test")),
                tests.length === 0 ? (React.createElement(Segment, { placeholder: true },
                    React.createElement(Header, { icon: true },
                        React.createElement(Icon, { name: "file outline" }),
                        "No tests created yet"),
                    React.createElement(Button, { primary: true, onClick: () => setCreateModalOpen(true) }, "Create Your First Test"))) : (React.createElement(List, { divided: true, relaxed: true }, tests.map((test) => (React.createElement(List.Item, { key: test.testId },
                    React.createElement(List.Content, null,
                        React.createElement(List.Header, { as: "a", onClick: () => navigate(`/test-ui/${test.testId}`), style: { cursor: 'pointer', color: '#2185d0' } }, test.testName),
                        React.createElement(List.Description, { style: { marginTop: '0.5rem' } },
                            React.createElement(Label, { size: "mini", color: test.method === 'GET' ? 'green' : test.method === 'POST' ? 'blue' : 'orange' }, test.method),
                            test.path,
                            React.createElement("span", { style: { marginLeft: '1rem', color: '#999' } },
                                React.createElement(Icon, { name: "clipboard check" }),
                                test.testCases.length,
                                " test case",
                                test.testCases.length !== 1 ? 's' : '')),
                        React.createElement("div", { style: { marginTop: '0.5rem' } },
                            React.createElement(Button, { size: "mini", onClick: () => navigate(`/test-ui/${test.testId}`) },
                                React.createElement(Icon, { name: "edit" }),
                                "Edit"),
                            React.createElement(DeleteButton, { onConfirm: () => handleDeleteTest(test.testId), style: { marginLeft: '0.5rem' }, title: "Delete Test", message: "Are you sure you want to delete this test? This action cannot be undone." }))))))))),
            React.createElement(Modal, { size: "small", open: createModalOpen, onClose: () => setCreateModalOpen(false) },
                React.createElement(Modal.Header, null,
                    React.createElement(Icon, { name: "plus" }),
                    "Create New Test"),
                React.createElement(Modal.Content, null,
                    React.createElement(Form, null,
                        React.createElement(Form.Field, null,
                            React.createElement("label", null, "Test Name"),
                            React.createElement(Input, { placeholder: "Enter test name...", value: newTestName, onChange: (e) => setNewTestName(e.target.value), onKeyPress: (e) => {
                                    if (e.key === 'Enter' && newTestName.trim()) {
                                        handleCreateTest();
                                    }
                                } })))),
                React.createElement(Modal.Actions, null,
                    React.createElement(Button, { onClick: () => setCreateModalOpen(false) }, "Cancel"),
                    React.createElement(Button, { primary: true, loading: creating, disabled: !newTestName.trim() || creating, onClick: handleCreateTest },
                        React.createElement(Icon, { name: "check" }),
                        "Create Test"))))));
};
